/** A name or id that points at another thing, as a real router link with a small type icon.
 *
 * Sites, suppliers, documents, records, filings, ledger entries, factors, people and sources all
 * read the same way in a table: an icon that says what kind of thing it is, the name as a visible
 * link, and optionally a second line (an id, a period). It is an `<a href>` (TanStack `Link`), so
 * middle-click, open-in-new-tab and keyboard Enter all work, and it is visibly a link (underline on
 * hover and focus, accent colour) rather than a bold word.
 *
 * `to` is a route path with `$param` placeholders, as `Link` takes it. The route tree's types are not
 * imported here on purpose: this primitive is used by every screen and must not depend on the table of
 * routes, so `to`, `params` and `search` are checked at the call site's own router instead.
 *
 * Inside a `DataTable` row, clicking the link follows it and does not also open the row.
 */

import {
  Buildings,
  CircleDashed,
  FileText,
  Flask,
  Handshake,
  LinkSimple,
  Plugs,
  Receipt,
  Scroll,
  UserCircle,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import styles from "./EntityLink.module.css";

export type EntityKind =
  | "site"
  | "supplier"
  | "document"
  | "record"
  | "filing"
  | "ledger"
  | "factor"
  | "person"
  | "source"
  | "other";

const ICON: Record<EntityKind, Icon> = {
  site: Buildings,
  supplier: Handshake,
  document: FileText,
  record: Receipt,
  filing: Scroll,
  ledger: LinkSimple,
  factor: Flask,
  person: UserCircle,
  source: Plugs,
  other: CircleDashed,
};

const KIND_WORD: Record<EntityKind, string> = {
  site: "Site",
  supplier: "Supplier",
  document: "Document",
  record: "Record",
  filing: "Filing",
  ledger: "Ledger entry",
  factor: "Factor",
  person: "Person",
  source: "Source",
  other: "Item",
};

export interface EntityLinkProps {
  kind: EntityKind;
  /** The visible name. */
  children: ReactNode;
  /** Route path, `$param` style: `/organisation/sites/$id`. */
  to: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
  /** A second line: an id, a period, a count. Muted, and part of the link's name. */
  secondary?: ReactNode;
  /** Say the kind in the accessible name (`Site: Trafford Park`). On by default. */
  announceKind?: boolean;
  testId?: string;
  "aria-describedby"?: string;
}

export function EntityLink({
  kind,
  children,
  to,
  params,
  search,
  secondary,
  announceKind = true,
  testId,
  "aria-describedby": describedBy,
}: EntityLinkProps) {
  const Glyph = ICON[kind];
  return (
    <Link
      // The route tree is the caller's: see the module note.
      to={to as never}
      params={params as never}
      search={search as never}
      className={styles.link}
      data-kind={kind}
      data-testid={testId}
      aria-describedby={describedBy}
    >
      <Glyph size={15} weight="regular" aria-hidden="true" className={styles.icon} />
      <span className={styles.text}>
        {announceKind ? (
          <>
            <span className="visuallyHidden">{KIND_WORD[kind]}:</span>{" "}
          </>
        ) : null}
        <span className={styles.name}>{children}</span>
        {secondary ? (
          <>
            {" "}
            <span className={styles.secondary}>{secondary}</span>
          </>
        ) : null}
      </span>
    </Link>
  );
}
